import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight, LogIn, UserPlus } from "lucide-react";
import Brand from "@/components/Brand";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(identifier, password);
      toast.success("Giriş başarılı");
      navigate(u.role === "admin" ? "/yonetimvolki35" : "/panel");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const forgot = () => {
    toast.info("Şifre sıfırlama için lütfen sistem yöneticisiyle iletişime geçin.");
  };

  return (
    <div className="min-h-screen brand-bg text-white auth-glow-bg">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-black/50 border-b border-white/10">
        <div className="max-w-md md:max-w-3xl mx-auto flex items-center justify-between px-5 h-16">
          <Link to="/login"><Brand size="sm" /></Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="w-9 h-9 border border-white/20 rounded-lg flex items-center justify-center text-white/70 hover:text-white hover:border-white/40 transition-colors" title="Giriş" data-testid="header-login">
              <LogIn className="w-4 h-4" />
            </Link>
            <Link to="/register" className="w-9 h-9 rounded-lg flex items-center justify-center btn-brand" title="Kayıt Ol" data-testid="header-register">
              <UserPlus className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-md md:max-w-lg mx-auto px-5 pt-8 pb-32">
        <div className="mb-8">
          <h1 className="font-display font-black text-3xl mb-2">Yeniden Hoş Geldiniz.</h1>
          <p className="text-white/60 text-sm leading-relaxed">
            Güvenli ve şifrelenmiş bağlantı üzerinden yönetim paneline erişim sağlıyorsunuz.
          </p>
        </div>

        <div className="border-t border-white/10 pt-8 mb-6">
          <h2 className="font-display font-bold text-2xl mb-1">Kullanıcı Girişi</h2>
          <p className="text-white/50 text-sm">Lütfen bilgilerinizi doğrulayın.</p>
        </div>

        <form onSubmit={submit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="text-sm text-white/80 mb-2 block">E-posta Adresi</label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="login-username-input"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="mail@ornek.com"
                required
                autoComplete="username"
                className="w-full bg-[hsl(0,0%,6%)] border border-white/10 focus:border-[hsl(217,91%,60%)] focus:ring-2 focus:ring-[hsl(217,91%,60%)]/30 outline-none pl-11 pr-4 py-3.5 text-sm rounded-2xl transition-colors"
              />
            </div>
            <div className="text-xs text-white/40 mt-1.5 ml-1">E-posta veya kullanıcı adınızı kullanabilirsiniz.</div>
          </div>

          <div>
            <label className="text-sm text-white/80 mb-2 block">Hesap Şifresi</label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                data-testid="login-password-input"
                type={showPw ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
                className="w-full bg-[hsl(0,0%,6%)] border border-white/10 focus:border-[hsl(217,91%,60%)] focus:ring-2 focus:ring-[hsl(217,91%,60%)]/30 outline-none pl-11 pr-11 py-3.5 text-sm rounded-2xl transition-colors"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                data-testid="toggle-password"
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white transition-colors"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between text-sm pt-1">
            <label className="inline-flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                data-testid="remember-me"
                className="w-4 h-4 rounded border-white/20 bg-transparent accent-[hsl(217,91%,60%)]"
              />
              <span className="text-white/70">Oturumu açık tut</span>
            </label>
            <button type="button" onClick={forgot} data-testid="forgot-password" className="text-[hsl(0,84%,66%)] hover:text-white transition-colors">
              Şifremi unuttum
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="btn-brand w-full font-bold text-base py-3.5 rounded-2xl inline-flex items-center justify-center gap-2 mt-2"
          >
            {loading ? "Giriş yapılıyor…" : (<>Giriş Yap <ArrowRight className="w-4 h-4" /></>)}
          </button>

          <div className="text-sm text-white/60 text-center pt-2">
            Henüz bir hesabınız yok mu?{" "}
            <Link to="/register" className="text-white font-bold hover:text-[hsl(217,91%,63%)] transition-colors" data-testid="link-register">
              Kayıt Olun
            </Link>
          </div>
        </form>
      </main>
    </div>
  );
}
